import { ChartOptions, TooltipItem } from "chart.js";
import { Context } from "chartjs-plugin-datalabels";
import { FC, memo, useMemo } from "react";
import { Bar } from "react-chartjs-2";

import { useRtl } from "@ryde/redux";
import { greyMui, rydeColors, rydeColorsGraphs } from "@ryde/theme";

import { useDashboardChartPoints } from "../../hooks/use-dashboard-chart-points";
import { BarChartConstants } from "./dashboard-bar.chart.constants";
import { IDashboardBarChartProps } from "./dashboard-bar.chart.models";
import {
  getBarLabelAlign,
  getInsideLabelFlags,
} from "./dashboard-bar.chart.utils";
import { DASHBOARD_CHART_BASE_OPTIONS } from "./dashboard-chart.constants";
import {
  dashboardCategoryAxis,
  dashboardValueAxis,
  defaultChartValueFormatter,
  isNoneDimensionLabel,
} from "./dashboard-chart.utils";

/**
 * Dashboard bar chart per the PRD/Figma. Vertical (column) bars in the brand
 * purple with a "None"/empty category in red; horizontal (row) bars in the
 * darker purple. In both, the value is shown inside the bar (white) or just
 * outside it when the bar is too short, with the metric and dimension on the
 * value / category axes and no legend.
 */
export const DashboardBarChart: FC<IDashboardBarChartProps> = memo(
  ({
    content,
    metricLabel,
    dimensionLabel,
    orientation = "vertical",
    valueFormatter,
  }) => {
    const { isRtl } = useRtl();
    const { labels, values, maxValue } = useDashboardChartPoints(content);
    const isHorizontal = orientation === "horizontal";
    const formatValue = useMemo(
      () => valueFormatter ?? defaultChartValueFormatter,
      [valueFormatter],
    );

    // Pre-computed per bar so the datalabels anchor/align/color callbacks below
    // just index into it, instead of re-deriving the ratio three times per bar.
    const insideLabelFlags = useMemo(
      () => getInsideLabelFlags(values, maxValue, orientation),
      [values, maxValue, orientation],
    );

    const data = useMemo(
      () => ({
        labels,
        datasets: [
          {
            label: metricLabel,
            data: values,
            backgroundColor: isHorizontal
              ? rydeColorsGraphs.horizontalBarDefaultColor
              : labels.map(label =>
                  isNoneDimensionLabel(label)
                    ? rydeColorsGraphs.verticalBarRedColor
                    : rydeColorsGraphs.verticalBarDefaultColor,
                ),
            borderRadius: 6,
            maxBarThickness: BarChartConstants.maxBarThickness[orientation],
            ...(isHorizontal
              ? {}
              : { categoryPercentage: 0.6, barPercentage: 0.9 }),
          },
        ],
      }),
      [isHorizontal, labels, values, metricLabel, orientation],
    );

    const options: ChartOptions<"bar"> = useMemo(() => {
      // Room for value labels drawn outside a short bar: to the right (LTR) or
      // left (RTL) for horizontal bars, above for vertical.
      const horizontalPadding = isRtl ? { left: 28 } : { right: 28 };
      const layoutPadding = isHorizontal ? horizontalPadding : { top: 20 };

      return {
        ...DASHBOARD_CHART_BASE_OPTIONS,
        indexAxis: isHorizontal ? "y" : "x",
        layout: { padding: layoutPadding },
        plugins: {
          legend: { display: false },
          tooltip: {
            rtl: isRtl,
            callbacks: {
              label: (item: TooltipItem<"bar">) =>
                `${metricLabel}: ${formatValue(Number(item.raw))}`,
            },
          },
          datalabels: {
            display: isHorizontal,
            // Centered/inside a tall enough bar; otherwise just outside a short one.
            anchor: isHorizontal
              ? "end"
              : (ctx: Context) =>
                  insideLabelFlags[ctx.dataIndex] ? "center" : "end",
            align: (ctx: Context) =>
              getBarLabelAlign(
                insideLabelFlags[ctx.dataIndex],
                isHorizontal,
                isRtl,
              ),
            clip: false,
            offset: BarChartConstants.datalabelOffset[orientation],
            color: (ctx: Context) =>
              insideLabelFlags[ctx.dataIndex] ? rydeColors.white : greyMui._700,
            font: { size: 11, weight: 600 },
            formatter: (value: number) => formatValue(value),
          },
        },
        scales: isHorizontal
          ? {
              x: dashboardValueAxis(metricLabel, { isRtl, isHorizontal: true }),
              y: dashboardCategoryAxis(dimensionLabel, {
                isRtl,
                isHorizontal: true,
              }),
            }
          : {
              x: dashboardCategoryAxis(dimensionLabel, { isRtl }),
              y: dashboardValueAxis(metricLabel, { isRtl }),
            },
      };
    }, [
      isHorizontal,
      orientation,
      metricLabel,
      dimensionLabel,
      isRtl,
      formatValue,
      insideLabelFlags,
    ]);

    return <Bar data={data} options={options} />;
  },
);

DashboardBarChart.displayName = "DashboardBarChart";
